import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { assetService } from '../../../services/assetService';
import { linkPreviewService } from '../../../services/linkPreviewService';

const AssetUploadModal = ({ onClose, onSuccess }) => {
  const [uploadType, setUploadType] = useState('file'); // 'file' or 'link'
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkPreview, setLinkPreview] = useState(null);
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'logo',
    campaign: '',
    orientation: 'landscape',
    dimensions: ''
  });

  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (!selectedFile) return;

    // Validate file size (50MB max)
    if (selectedFile?.size > 52428800) {
      setError('File size must be less than 50MB');
      return;
    }

    setFile(selectedFile);
    setError('');

    // Generate preview for images
    if (selectedFile?.type?.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader?.result);
      };
      reader?.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }

    // Auto-fill title from filename
    if (!formData?.title) {
      const fileName = selectedFile?.name?.replace(/\.[^/.]+$/, '');
      setFormData(prev => ({ ...prev, title: fileName }));
    }
  };

  const handleLinkUrlChange = async (e) => {
    const url = e?.target?.value;
    setLinkUrl(url);
    setError('');
    
    // Clear previous preview
    setLinkPreview(null);
  };

  const handleFetchPreview = async () => {
    if (!linkUrl?.trim()) {
      setError('Please enter a URL');
      return;
    }

    if (!linkPreviewService?.isValidUrl(linkUrl)) {
      setError('Please enter a valid URL (must start with http:// or https://)');
      return;
    }

    try {
      setFetchingPreview(true);
      setError('');
      
      const previewData = await linkPreviewService?.fetchPreview(linkUrl);
      setLinkPreview(previewData);
      
      // Auto-fill form fields from preview
      if (!formData?.title && previewData?.title) {
        setFormData(prev => ({ ...prev, title: previewData?.title }));
      }
      if (!formData?.description && previewData?.description) {
        setFormData(prev => ({ ...prev, description: previewData?.description }));
      }
    } catch (err) {
      setError(err?.message || 'Failed to fetch link preview');
    } finally {
      setFetchingPreview(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (uploadType === 'file' && !file) {
      setError('Please select a file');
      return;
    }

    if (uploadType === 'link' && !linkUrl?.trim()) {
      setError('Please enter a URL');
      return;
    }

    if (!formData?.title?.trim()) {
      setError('Please enter a title');
      return;
    }

    try {
      setUploading(true);
      setError('');

      if (uploadType === 'file') {
        const fileType = file?.type?.startsWith('video/') ? 'video' : 'image';
        await assetService?.upload(file, {
          ...formData,
          fileType
        });
      } else {
        // Upload link asset
        await assetService?.uploadLink({
          ...formData,
          linkUrl,
          linkPreview
        });
      }

      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to upload asset');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">Upload New Asset</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Upload Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Upload Type *
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setUploadType('file');
                  setError('');
                  setLinkPreview(null);
                }}
                className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                  uploadType === 'file' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Icon name="Upload" size={20} />
                  <span className="font-medium">Upload File</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => {
                  setUploadType('link');
                  setError('');
                  setFile(null);
                  setPreview(null);
                }}
                className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                  uploadType === 'link' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Icon name="Link" size={20} />
                  <span className="font-medium">Add Link</span>
                </div>
              </button>
            </div>
          </div>

          {/* File Upload Section */}
          {uploadType === 'file' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                File *
              </label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer">
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*,video/*,application/pdf"
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {preview ? (
                    <img src={preview} alt="Preview" className="max-h-48 mx-auto mb-3 rounded" />
                  ) : (
                    <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-3" />
                  )}
                  <p className="text-foreground font-medium mb-1">
                    {file ? file?.name : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Images, videos, or PDFs (max 50MB)
                  </p>
                </label>
              </div>
            </div>
          )}

          {/* Link Input Section */}
          {uploadType === 'link' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Link URL *
                </label>
                <div className="flex gap-2">
                  <Input
                    value={linkUrl}
                    onChange={handleLinkUrlChange}
                    placeholder="https://example.com/article"
                    type="url"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleFetchPreview}
                    loading={fetchingPreview}
                    disabled={fetchingPreview || !linkUrl?.trim()}
                    iconName="Eye"
                    variant="outline"
                  >
                    Preview
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Paste a URL to automatically generate a preview
                </p>
              </div>

              {/* Link Preview Display */}
              {linkPreview && (
                <div className="border border-border rounded-lg p-4 bg-muted/30">
                  <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
                    Link Preview
                  </p>
                  <div className="flex gap-4">
                    {linkPreview?.image && (
                      <div className="flex-shrink-0">
                        <img
                          src={linkPreview?.image}
                          alt="Preview"
                          className="w-24 h-24 object-cover rounded border border-border"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-foreground mb-1 line-clamp-2">
                        {linkPreview?.title}
                      </h4>
                      {linkPreview?.description && (
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                          {linkPreview?.description}
                        </p>
                      )}
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Icon name="Globe" size={12} />
                        <span>{linkPreview?.domain}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Title *
            </label>
            <Input
              value={formData?.title}
              onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
              placeholder="Enter asset title"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              placeholder="Enter asset description"
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Category *
            </label>
            <select
              value={formData?.category}
              onChange={(e) => setFormData({ ...formData, category: e?.target?.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              required
            >
              <option value="logo">Logo</option>
              <option value="technology">Technology</option>
              <option value="photo">Photo</option>
              <option value="diagram">Diagram</option>
              <option value="video">Video</option>
            </select>
          </div>

          {/* Campaign */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Campaign
            </label>
            <Input
              value={formData?.campaign}
              onChange={(e) => setFormData({ ...formData, campaign: e?.target?.value })}
              placeholder="e.g., Q1 2026 Launch"
            />
          </div>

          {/* Orientation */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Orientation
            </label>
            <select
              value={formData?.orientation}
              onChange={(e) => setFormData({ ...formData, orientation: e?.target?.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="landscape">Landscape</option>
              <option value="portrait">Portrait</option>
              <option value="square">Square</option>
            </select>
          </div>

          {/* Dimensions */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Dimensions
            </label>
            <Input
              value={formData?.dimensions}
              onChange={(e) => setFormData({ ...formData, dimensions: e?.target?.value })}
              placeholder="e.g., 1920 × 1080 px"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive rounded-lg">
              <Icon name="AlertCircle" size={20} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              loading={uploading}
              disabled={uploading}
              iconName={uploadType === 'file' ? 'Upload' : 'Link'}
              iconPosition="left"
              variant="outline"
              className="flex-1"
            >
              {uploading ? 'Uploading...' : uploadType === 'file' ? 'Upload Asset' : 'Add Link'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssetUploadModal;